import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { RewardTableItem } from "./RewardTableItem";
import { RewardsTableSkeleton } from "./RewardsTableSkeleton";
import { type RewardData } from "@/components/referrals/tableConfigs";

interface RewardsTableProps {
  rewards: RewardData[];
  onRedeemClick?: (reward: RewardData) => void;
  itemsPerPage?: number;
  showPagination?: boolean;
  isLoading?: boolean;
}

export function RewardsTable({ 
  rewards, 
  onRedeemClick,
  itemsPerPage = 10,
  showPagination = true,
  isLoading = false
}: RewardsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(itemsPerPage);

  // Calculate pagination
  const totalPages = Math.ceil(rewards.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentRewards = rewards.slice(startIndex, endIndex);

  // Reset to page 1 when rewards change
  useMemo(() => {
    setCurrentPage(1);
  }, [rewards.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  // Show skeleton loading state
  if (isLoading) {
    return <RewardsTableSkeleton rows={pageSize} />;
  }

  // Show empty state
  if (rewards.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-500 space-grotesk">No rewards found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Desktop Table Header */}
      <div className="hidden lg:grid grid-cols-6 py-3 px-4 gap-2 bg-[#F6F6F6] border border-[#EAEAEA] text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal">
        <div>CANDIDATE NAME</div>
        <div>JOB TITLE</div>
        <div>AMOUNT</div>
        <div>STATUS</div>
        <div>DATE</div>
        <div className="text-center">ACTIONS</div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-200">
        {currentRewards.map((reward, index) => (
          <RewardTableItem
            key={reward.id}
            reward={reward}
            index={startIndex + index}
            onRedeemClick={onRedeemClick}
          />
        ))}
      </div>

      {/* Pagination */}
      {showPagination && rewards.length > pageSize && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="space-grotesk text-gray-600 disabled:text-gray-400"
            >
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((page) => (
                <Button
                  key={page}
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 p-0 space-grotesk rounded-lg font-medium ${
                    currentPage === page 
                      ? "bg-orange-500 text-white" 
                      : "text-gray-600 bg-white border border-[#EAEAEA]"
                  }`}
                >
                  {page.toString().padStart(2, '0')}
                </Button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="space-grotesk text-gray-600 disabled:text-gray-400"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
